import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { Upload, Download } from 'lucide-react';
import Papa from 'papaparse';
import axios from 'axios';
import { sortObjectOptions } from '@/utils/sortOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OfflineAttendancePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [trainingTopics, setTrainingTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [newTopicName, setNewTopicName] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [notes, setNotes] = useState('');
  const [fileContent, setFileContent] = useState(null);
  const [fileName, setFileName] = useState('');
  
  useEffect(() => {
    fetchTrainingTopics();
  }, []);
  
  const fetchTrainingTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingTopics(response.data);
    } catch (error) {
      console.error('Failed to fetch topics');
    }
  };
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setFileName(file.name);
    
    Papa.parse(file, {
      complete: (result) => {
        setFileContent(result.data);
        toast.success('File loaded successfully');
      },
      header: true,
      skipEmptyLines: true
    });
  };
  
  const handleDownloadTemplate = () => {
    const csvContent = 'employee_email,attendance_status\njohn@example.com,present\njane@example.com,absent';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'attendance_template.csv';
    link.click();
  };
  
  const handleSubmit = async () => {
    if (!fileContent) {
      toast.error('Please upload an attendance file');
      return;
    }
    
    if (!sessionDate) {
      toast.error('Please select session date');
      return;
    }
    
    if (!selectedTopicId && !newTopicName) {
      toast.error('Please select a chapter or enter new topic name');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Convert file content back to CSV string
      const csvString = Papa.unparse(fileContent);
      
      const response = await axios.post(
        `${API}/hospital/attendance/upload`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            file_content: csvString,
            training_topic_id: selectedTopicId || undefined,
            training_topic_name: newTopicName || undefined,
            session_date: sessionDate,
            notes: notes || undefined
          }
        }
      );
      
      if (response.data.error_count > 0) {
        toast.warning(`Processed ${response.data.processed_count} records. ${response.data.error_count} errors.`);
      } else {
        toast.success(`Attendance uploaded successfully. ${response.data.processed_count} records processed.`);
      }
      
      // Reset form
      setFileContent(null);
      setFileName('');
      setSelectedTopicId('');
      setNewTopicName('');
      setSessionDate('');
      setNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attendance');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <AdminLayout>
      <div className="p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Upload Offline Attendance</h1>
      
        <div className="max-w-3xl">
          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="space-y-6">
            <div>
              <Label>Training Chapter *</Label>
              <Select value={selectedTopicId} onValueChange={setSelectedTopicId}>
                <SelectTrigger><SelectValue placeholder="Select existing topic" /></SelectTrigger>
                <SelectContent>
                  {sortObjectOptions(trainingTopics, 'title').map(topic => (
                    <SelectItem key={topic.id} value={topic.id}>{topic.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-1">Or create new topic:</p>
              <Input
                className="mt-2"
                value={newTopicName}
                onChange={(e) => setNewTopicName(e.target.value)}
                placeholder="Enter new topic name"
                disabled={!!selectedTopicId}
              />
            </div>
            
            <div>
              <Label>Session Date *</Label>
              <Input
                type="date"
                value={sessionDate}
                onChange={(e) => setSessionDate(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Upload Attendance File (CSV) *</Label>
              <div className="mt-2 flex items-center space-x-2">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="flex-1"
                />
                <Button onClick={handleDownloadTemplate} variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" /> Template
                </Button>
              </div>
              {fileName && <p className="text-sm text-green-600 mt-1">✓ {fileName} loaded</p>}
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this training session"
                rows={3}
              />
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>CSV Format:</strong> File must contain columns: <code>employee_email</code>, <code>attendance_status</code> (present/absent)
              </p>
            </div>
            
            <div className="flex space-x-4">
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-[#0056B3] hover:bg-[#0056B3]/90"
                disabled={loading}
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Upload Attendance</>
                )}
              </Button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default OfflineAttendancePage;